import React, { useEffect, useState } from "react";
import PropTypes from "prop-types";
import {
  Modal,
  Box,
  Typography,
  Button,
  Avatar,
  IconButton,
  Chip,
  Snackbar,
  useTheme,
  List,
} from "@mui/material";
import {
  Close,
  ArrowForwardIos,
  ArrowBackIos,
  Check,
  LocationOn,
  Web,
  OpenInBrowser,
} from "@mui/icons-material";
import { gql, useMutation, useQuery } from "@apollo/client";
import { useTranslation } from "react-i18next";
import MDEditor from "@uiw/react-md-editor";
import TeamDetails from "./TeamDetails";
import ProductDetails from "./ProductDetails";

const ADD_CONNECTION = gql`
  mutation AddToConnection($attendeeId: ID!, $attendeeRole: String) {
    addToConnection(attendeeId: $attendeeId, attendeeRole: $attendeeRole) {
      success
      message
    }
  }
`;

const REMOVE_CONNECTION = gql`
  mutation RemoveFromConnection($attendeeId: ID!, $attendeeRole: String) {
    removeFromConnection(attendeeId: $attendeeId, attendeeRole: $attendeeRole) {
      success
      message
    }
  }
`;
const GET_EXHIBITOR_PRODUCTS = gql`
  query getMyProduct($exhibitor_id: ID!) {
    getMyProduct(exhibitor_id: $exhibitor_id) {
      id
      product_name
      name
      designation
      company
      country
      role
      email
      stallNo
      bio
      product_image
      product_description
      exhibitor_id
      isConnected
      created
      profile_pic
    }
  }
`;

const GET_EXHIBITOR_TEAM = gql`
  query getMyTeam($stallNo: String!) {
    getMyTeam(stallNo: $stallNo) {
      id
      name
      email
      designation
      bio
      profile_pic
      isConnected
    }
  }
`;
const SponsorModal = ({
  refresh,
  open = false,
  onClose,
  exhibitor,
  exhibitors,
  role,
}) => {
  const theme = useTheme();
  const { t, i18n } = useTranslation();
  const [allProductData, setAllProductData] = useState([]);
  const [allTeamData, setAllTeamData] = useState([]);
  const [isConnected, setIsConnected] = useState(exhibitor.isConnected);
  const [currentExhibitor, setCurrentExhibitor] = useState(exhibitor);
  const [direction, setDirection] = useState("next");
  const [filteredExhibitors, setFilteredExhibitors] = useState(exhibitors);
  const [showSuccess, setShowSuccess] = useState(false);
  const [showRemove, setShowRemove] = useState(false);
  const [addConnection] = useMutation(ADD_CONNECTION);
  const [removeConnection] = useMutation(REMOVE_CONNECTION);

  const { data: productData } = useQuery(GET_EXHIBITOR_PRODUCTS, {
    variables: { exhibitor_id: exhibitor.id },
    skip: !exhibitor.id,
    fetchPolicy: "cache-and-network",
  });

  const { data: teamData } = useQuery(GET_EXHIBITOR_TEAM, {
    variables: { stallNo: exhibitor.stallNo },
    skip: !exhibitor.stallNo,
    fetchPolicy: "cache-and-network",
  });

  const handleConnectionToggle = async (id) => {
    try {
      if (isConnected) {
        const { data } = await removeConnection({
          variables: { attendeeId: id, attendeeRole: "exhibitor" },
        });
        if (data?.removeFromConnection?.success) {
          setIsConnected(false);
          setShowRemove(true);
          setTimeout(() => {
            setShowRemove(false);
            refresh();
          }, 2000);
        }
      } else {
        const { data } = await addConnection({
          variables: { attendeeId: id, attendeeRole: "exhibitor" },
        });
        if (data?.addToConnection?.success) {
          setIsConnected(true);
          setShowSuccess(true);
          setTimeout(() => {
            setShowSuccess(false);
            refresh();
          }, 2000);
        }
      }
    } catch (error) {
      console.error("Error updating connection:", error);
    }
  };

  // Properly handle product data updates with useEffect
  useEffect(() => {
    if (productData) {
      const parseData =
        typeof productData.getMyProduct === "string"
          ? JSON.parse(productData.getMyProduct)
          : productData.getMyProduct;
      setAllProductData(parseData);
    }
  }, [productData]);

  // Properly handle team data updates with useEffect
  useEffect(() => {
    if (teamData) {
      const parseData =
        typeof teamData.getMyTeam === "string"
          ? JSON.parse(teamData.getMyTeam)
          : teamData.getMyTeam;
      setAllTeamData(parseData);
    }
  }, [teamData]);

  // console.log(currentExhibitor)

  const renderDetails = () => (
    <>
      <Typography variant="h5" fontWeight="bold" gutterBottom>
        {currentExhibitor.company_name}
      </Typography>
      {currentExhibitor.sponsorship_level && (
        <Chip
          label={currentExhibitor.sponsorship_level}
          color="secondary"
          size="small"
        />
      )}

      {/* {currentExhibitor.stallNo && (
                <Chip
                    label={`${t('Stall')}: ${currentExhibitor.stallNo}`}
                    color="primary"
                    sx={{ mb: 2 }}
                />
            )} */}

      {currentExhibitor.fascia_board && (
        <Typography variant="subtitle1" color="textSecondary" gutterBottom>
          Fascia Board: {currentExhibitor.fascia_board}
        </Typography>
      )}
      {currentExhibitor.website && (
        <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2 }}>
          <List
            sx={{ display: "flex", gap: 1, textDecoration: "none" }}
            component={"a"}
            href={currentExhibitor.website}
            target="_blank"
            rel="noopener noreferrer"
          >
            <OpenInBrowser fontSize="small" color="action" />
            <Typography variant="body1" color="textSecondary">
              {currentExhibitor.website}
            </Typography>
          </List>
        </Box>
      )}

      <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2 }}>
        <LocationOn fontSize="small" color="action" />
        <Typography variant="body1" color="textSecondary">
          {currentExhibitor.country}
        </Typography>
      </Box>

      {currentExhibitor.brands && (
        <Box sx={{ mb: 2 }}>
          <Typography variant="subtitle2" gutterBottom>
            {t("Brands")}:
          </Typography>
          <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
            {currentExhibitor.brands.split(",").map((brand, index) => (
              <Chip key={index} label={brand.trim()} color="secondary" />
            ))}
          </Box>
        </Box>
      )}

      {currentExhibitor.company_profile && (
        <Box
          sx={{
            mt: 2,
            p: 2,
            border: `1px solid ${theme.palette.divider}`,
            borderRadius: 2,
            maxHeight: 300,
            overflowY: "auto",
          }}
        >
          <Typography variant="subtitle2" gutterBottom>
            {t("About")} {currentExhibitor.company_name}
          </Typography>
          <MDEditor.Markdown
            source={currentExhibitor.company_profile}
            style={{
              padding: 6,
              backgroundColor: theme.palette.background.default,
              borderRadius: 4,
              caretColor: theme.palette.primary.main,
              color: theme.palette.text.primary,
            }}
          />
        </Box>
      )}
    </>
  );

  return (
    <>
      <Snackbar
        open={showSuccess || showRemove}
        autoHideDuration={2000}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
        sx={{ mt: 6 }}
      >
        <Box
          sx={{
            bgcolor: showSuccess ? "success.main" : "error.main",
            color: "white",
            p: 2,
            borderRadius: 2,
            display: "flex",
            alignItems: "center",
            gap: 1,
          }}
        >
          <Check fontSize="small" />
          <Typography variant="body2" sx={{ color: "white" }}>
            {showSuccess
              ? t("sponsorModal.addedSuccess")
              : t("sponsorModal.removedSuccess")}
          </Typography>
        </Box>
      </Snackbar>
      <Modal
        open={open}
        onClose={() => {
          onClose();
          refresh();
        }}
        dir={i18n.dir()}
      >
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "end",
            height: "100vh",
            outline: "none",
            position: "relative",
          }}
        >
          <Box
            sx={{
              width: { xs: "100%", md: 600 },
              bgcolor: "background.paper",
              boxShadow: 24,
              p: 2,
              borderRadius: 4,
              position: "relative",
              maxHeight: "90vh",
              overflowY: "auto",
            }}
          >
            <Box sx={{ position: "sticky", top: 0, zIndex: 1, pb: 2 }}>
              <Box sx={{ display: "flex", alignItems: "center" }}>
                {/* {role === "my_products" && (
                                <Box sx={{ display: 'flex', gap: 1 }}>
                                    <IconButton
                                        onClick={handleEditClick}
                                        color="primary"
                                        size="small"
                                    >
                                        <Edit />
                                    </IconButton>
                                    <IconButton
                                        onClick={handleDelete}
                                        color="error"
                                        size="small"
                                    >
                                        <Delete />
                                    </IconButton>
                                </Box>
                            )} */}
                <IconButton
                  sx={{ position: "absolute", top: 0, right: 0 }}
                  onClick={() => {
                    onClose();
                    refresh();
                  }}
                >
                  <Close />
                </IconButton>
              </Box>
            </Box>

            <Box
              component="img"
              src={currentExhibitor.profile_pic || "./profile_pic.png"}
              alt={currentExhibitor.company_name}
              sx={{
                width: "100%",
                height: 200,
                objectFit: "contain",
                mx: "auto",
                mb: 3,
                p: 2,
              }}
            />

            {renderDetails()}
            {allTeamData && allTeamData?.length > 0 && (
              <TeamDetails
                teamData={allTeamData}
                handleToConnect={handleConnectionToggle}
                isConnected={isConnected}
              />
            )}
            {/* {allProductData && allProductData?.length > 0 && <ProductDetails productData={allProductData} />} */}

            <Box
              sx={{ mt: 3, display: "flex", justifyContent: "space-between" }}
            >
              <Button
                variant="contained"
                onClick={onClose}
                sx={{ flex: 1, mr: 1 }}
              >
                {t("Close")}
              </Button>
            </Box>
          </Box>
        </Box>
      </Modal>
    </>
  );
};

SponsorModal.propTypes = {
  refresh: PropTypes.func.isRequired,
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  exhibitor: PropTypes.object.isRequired,
  exhibitors: PropTypes.array.isRequired,
  role: PropTypes.string.isRequired,
};

export default SponsorModal;
